import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../context/AuthContext';
import axios from 'axios';
import {
  FileText,
  Upload,
  Clock,
  CheckCircle,
  AlertCircle,
  RefreshCw,
  Image,
  File,
  Download,
} from 'lucide-react';
import { Button } from '../../../components/ui/button';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

const statusConfig = {
  pending: { label: 'Requested', color: 'bg-amber-100 text-amber-700', icon: Clock },
  viewed: { label: 'Viewed', color: 'bg-emerald-100 text-emerald-700', icon: FileText },
  fulfilled: { label: 'Fulfilled', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
  partially_fulfilled: { label: 'Partial', color: 'bg-amber-100 text-amber-700', icon: AlertCircle },
};

const uploadStatusConfig = {
  pending: { label: 'Under Review', color: 'bg-amber-100 text-amber-700' },
  verified: { label: 'Verified', color: 'bg-emerald-100 text-emerald-700' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700' },
};

/**
 * Managed Client Documents Page - Phase 7
 * 
 * Document requests and uploads for managed clients.
 * Shows pending requests and upload history.
 */
const ManagedClientDocumentsPage = () => {
  const { token } = useAuth();
  const [requests, setRequests] = useState([]);
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('requests');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [requestsRes, uploadsRes] = await Promise.all([
        axios.get(`${API}/portal/managed/document-requests`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API}/portal/managed/uploads`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      setRequests(requestsRes.data.items || []);
      setUploads(uploadsRes.data.items || []);
    } catch (err) {
      console.error('Failed to fetch documents:', err);
      setError(err.response?.data?.detail || 'Failed to load documents');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) fetchData();
  }, [token]);

  const pendingRequests = requests.filter(r => r.status === 'pending' || r.status === 'viewed');

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    );
  }

  const getFileIcon = (mimeType) => {
    if (mimeType?.startsWith('image/')) return Image;
    return File;
  };

  return (
    <div className="space-y-6" data-testid="managed-client-documents">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">Documents</h1>
          <p className="text-slate-500 mt-1">View requested documents and uploads</p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw size={16} className="mr-2" />
          Refresh
        </Button>
      </div>

      {/* Alert for pending requests */}
      {pendingRequests.length > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-4">
          <AlertCircle className="text-amber-500 flex-shrink-0 mt-0.5" size={24} />
          <div>
            <h3 className="font-semibold text-amber-800">Documents Needed</h3>
            <p className="text-amber-700 text-sm mt-1">
              Your specialist has requested {pendingRequests.length} document(s). 
              Please upload them to continue with your case.
            </p>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        <button
          onClick={() => setActiveTab('requests')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'requests'
              ? 'border-emerald-600 text-emerald-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          Requests ({requests.length})
        </button>
        <button
          onClick={() => setActiveTab('uploads')}
          className={`px-4 py-2 font-medium text-sm border-b-2 transition-colors ${
            activeTab === 'uploads'
              ? 'border-emerald-600 text-emerald-600'
              : 'border-transparent text-slate-500 hover:text-slate-700'
          }`}
        >
          My Uploads ({uploads.length})
        </button>
      </div>

      {/* Requests Tab */}
      {activeTab === 'requests' && (
        <div>
          {requests.length > 0 ? (
            <div className="space-y-4">
              {requests.map((request) => {
                const status = statusConfig[request.status] || statusConfig.pending;
                const StatusIcon = status.icon;
                const isPending = request.status === 'pending' || request.status === 'viewed';
                return (
                  <div 
                    key={request.request_id}
                    className={`bg-white rounded-xl border p-5 ${isPending ? 'border-amber-300' : 'border-slate-200'}`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-4">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${isPending ? 'bg-amber-100' : 'bg-slate-100'}`}>
                          <FileText className={isPending ? 'text-amber-600' : 'text-slate-600'} size={24} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-stone-900">{request.document_type || 'Document'}</h3>
                          {request.description && (
                            <p className="text-sm text-slate-500 mt-1">{request.description}</p>
                          )}
                          {request.due_date && (
                            <p className="text-xs text-slate-400 mt-2">
                              Due by: {new Date(request.due_date).toLocaleDateString()}
                            </p>
                          )}
                        </div>
                      </div>
                      <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${status.color}`}>
                        <StatusIcon size={14} />
                        {status.label}
                      </span>
                    </div>
                    {isPending && (
                      <div className="mt-4 pt-4 border-t border-slate-100">
                        <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700">
                          <Upload size={16} className="mr-2" />
                          Upload Document
                        </Button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <FileText size={48} className="mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-stone-900 mb-2">No Document Requests</h3>
              <p className="text-slate-500">
                Your specialist hasn't requested any documents yet.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Uploads Tab */}
      {activeTab === 'uploads' && (
        <div>
          {uploads.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {uploads.map((upload) => {
                const status = uploadStatusConfig[upload.verification_status] || uploadStatusConfig.pending;
                const FileIcon = getFileIcon(upload.mime_type);
                return (
                  <div 
                    key={upload.upload_id}
                    className="bg-white rounded-xl border border-slate-200 p-5"
                  >
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                        <FileIcon className="text-slate-600" size={24} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-stone-900 truncate">{upload.file_name || 'Document'}</h3>
                        <p className="text-sm text-slate-500">{upload.document_type || 'Document'}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${status.color}`}>
                            {status.label}
                          </span>
                          <span className="text-xs text-slate-400">
                            {upload.created_at ? new Date(upload.created_at).toLocaleDateString() : ''}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 p-12 text-center">
              <Upload size={48} className="mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold text-stone-900 mb-2">No Uploads Yet</h3>
              <p className="text-slate-500">
                Documents you upload will appear here.
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ManagedClientDocumentsPage;
